import { ListHistory, SegmentHistory } from '@dev/ee-api/ee-api-models';
import {
    ArrowDropDownIcon,
    DateDropdown,
    LineChart,
    palette,
    SecondaryButton,
    SimplePopover,
} from '@dev/ee-components';
import { Paper } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import dayjs from 'dayjs';
import { find, truncate } from 'lodash-es';
import React, { ChangeEvent, FC, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'react-recompose';
import styled from 'styled-components';

import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { useDateFilterData } from '@/services/tools/date';

import { LineChartPannelProps } from './ContactsHistoryInterface';

const GraphTitle = styled.h2`
    color: ${palette.jacarta[5]};
    font-size: 19px;
    font-family: Nunito;
    font-weight: bold;
`;

const TitleRow = styled.div`
    display: flex;
    padding: 24px 48px;
    justify-content: space-between;
    border-bottom: 1px solid ${palette.gray[2]};
`;

const LineChartPannelContent = styled.div`
    padding: 32px 48px;
`;
const ArrowDropdownContainer = styled.div`
    margin-bottom: -4px;
`;

const styles = {
    paperRoot: {
        boxShadow: 'none',
        marginTop: '32px',
    },
    chartTitle: {
        fontSize: '13px',
        fontFamily: 'Nunito',
        color: palette.jacarta[5],
        fontWeight: 600,
        marginBottom: '28px',
    },
    chartWrapper: {
        marginBottom: '32px',
    },
    pannelButtonsWrapper: {
        display: 'flex',
        alignItems: 'center',
    },
    dateDropdownWrapper: {
        marginLeft: '8px',
    },
};

const DATE_FORMAT = 'DD MMM YYYY';

/**
 *
 * @param pickedSegmentNames names of segments/lists for which line charts should be generated
 * @param isToday if To date should be today
 * @param classes classes
 * @param props Line Chart Panel Props
 * @returns
 */

const prepareGraphData = (
    segment: SegmentHistory[] | ListHistory[],
    from: string
): SegmentHistory[] | ListHistory[] => {
    if (segment?.length) {
        const fromDate = dayjs(from).add(1, 'day').startOf('day');
        const firstDataDate = dayjs(segment[0].Day).startOf('day');
        const missingDays = firstDataDate.diff(fromDate, 'day');
        let missingDaysArray: SegmentHistory[] | ListHistory[] = [];
        if (missingDays > 0) {
            missingDaysArray = new Array(missingDays).fill(1).map((value: number, index) => ({
                Day: firstDataDate.subtract(1 + index, 'day').format('MM-DD'),
                Count: 0,
            }));
            missingDaysArray = missingDaysArray.reverse();
        }
        const graphData = segment.map((entry): SegmentHistory | ListHistory => ({
            Day: dayjs(entry.Day).format('MM-DD'),
            Count: entry.Count,
        }));
        return [...missingDaysArray, ...graphData];
    }
    return [];
};

const generateHistoryLineChart = (
    pickedSegmentNames: string[],
    isToday: boolean,
    classes: any,
    props: LineChartPannelProps,
    from: string,
    allContactsLabel: string
): JSX.Element[] => {
    const { t } = props;
    return pickedSegmentNames?.map((pickedSegmentName: string, index: number): JSX.Element => {
        const segment = find(
            props.graphsData,
            (segment): boolean => segment?.Name === pickedSegmentName || segment?.ListName === pickedSegmentName
        );
        const chartTitle =
            pickedSegmentName === allContactsLabel ? t('allcontacts') : segment?.Name || segment?.ListName;

        let formattedData;
        let isGraphLoading = true;

        if (segment) {
            formattedData = prepareGraphData(segment?.History, from);
            isGraphLoading = false;
        }

        return (
            <div key={`chart-${segment?.Name || segment?.ListName}-${index}`} className={classes.chartWrapper}>
                <div className={classes.chartTitle}>{chartTitle}</div>
                <LineChart
                    data={formattedData}
                    fullWidth
                    isEmpty={!segment.History?.length}
                    isLoading={isGraphLoading}
                    height={250}
                    lineConfig={[
                        {
                            color: palette.indigo[6],
                            dataKey: 'Count',
                        },
                    ]}
                    padding={{
                        left: 10,
                        right: 10,
                    }}
                    showGrid
                    showLegend={false}
                    showTooltip
                    startFromZero={false}
                    xMinTickGap={30}
                    XAxisProps={{
                        interval: 'preserveEnd',
                    }}
                    xAxisGroupName="Day"
                    emptyDescription={isToday ? t('trackingdatanotavailable') : t('notrackinghistorytodisplay')}
                />
            </div>
        );
    });
};

/**
 *
 * @param props Line chart props
 * @returns Single Line chart
 */
const LineChartPannel: FC<LineChartPannelProps> = (props: LineChartPannelProps): JSX.Element => {
    const { graphsData, classes, title } = props;
    const dateOptions = useDateFilterData();
    const dateNow = dayjs();
    const [state, setState] = useState({
        pickedSegmentNames: graphsData.length ? [graphsData[0]?.Name || graphsData[0]?.ListName] : [],
        from: dateNow.subtract(6, 'day').format(),
        to: dateNow.format(),
        isToday: true,
        dateDropdownValue: 7,
        allContactsLabel: graphsData.length && (graphsData[0].IsAllContacts as boolean) && graphsData?.[0]?.Name,
    });

    const dateDropdownOptions = dateOptions?.filter(({ value }) => value !== DateDropdownOptions.All);

    const pickSegmnetsOptions = !graphsData
        ? null
        : graphsData?.map((graphData): { label: string; value: any; isAllContacts: boolean } => ({
              label:
                  truncate(graphData?.Name, { length: 20, omission: '...' }) ||
                  truncate(graphData?.ListName, { length: 20, omission: '...' }),
              value: graphData?.Name || graphData?.ListName,
              isAllContacts: graphData.IsAllContacts,
          }));

    const pickSegment = (ev: ChangeEvent<HTMLInputElement>, selected: string[]): void => {
        setState({
            ...state,
            pickedSegmentNames: selected,
        });
    };

    const pickDateDropdownNonCalendarOption = (selected: Record<string, any>): void => {
        const { loadSegmentsFunction } = props;
        const dateNow = dayjs();
        const from = dateNow.subtract(selected.value - 1, 'day').format();
        const to = dateNow.format();
        const isToday = true;
        loadSegmentsFunction(true, from, to);
        setState({
            ...state,
            from,
            to,
            isToday,
            dateDropdownValue: selected.value,
        });
    };

    const pickDateDropdownCalendarOption = (dateFrom: string, dateTo: string): void => {
        const { loadSegmentsFunction } = props;
        const from = dayjs(dateFrom).format();
        const to = dayjs(dateTo).format();
        const isToday = dayjs(dateTo) === dayjs();
        loadSegmentsFunction(true, from, to);
        setState({
            ...state,
            from,
            to,
            isToday,
        });
    };
    return (
        <Paper classes={{ root: classes.paperRoot }} className="board">
            <TitleRow>
                <GraphTitle>{title}</GraphTitle>
                <div className={classes.pannelButtonsWrapper}>
                    {pickSegmnetsOptions.length > 1 && (
                        <SimplePopover
                            id="contacts-history-segments"
                            type="custom"
                            options={pickSegmnetsOptions}
                            defaultCheckboxValue={state.pickedSegmentNames}
                            enableCheckbox
                            onCheckboxClick={pickSegment}
                            popperProps={{ disablePortal: false }}
                        >
                            <SecondaryButton
                                id="contacts-history-segments-button"
                                endIcon={
                                    <ArrowDropdownContainer>
                                        <ArrowDropDownIcon color={palette.indigo[6]} />
                                    </ArrowDropdownContainer>
                                }
                                text={title}
                            />
                        </SimplePopover>
                    )}
                    <div className={classes.dateDropdownWrapper}>
                        <DateDropdown
                            id="contact-history"
                            displayDateFormat={DATE_FORMAT}
                            options={dateDropdownOptions}
                            type="input"
                            onItemClick={(e, obj): void => {
                                pickDateDropdownNonCalendarOption(obj);
                            }}
                            calendarOnChange={(start, end): void => {
                                pickDateDropdownCalendarOption(start, end);
                            }}
                            value={state.dateDropdownValue}
                            returnRawData={true}
                            buttonVariant="secondary"
                            placement="bottom-end"
                            maxDate={dateNow.toString()}
                        />
                    </div>
                </div>
            </TitleRow>
            <LineChartPannelContent>
                {state.pickedSegmentNames.length
                    ? generateHistoryLineChart(
                          state.pickedSegmentNames,
                          state.isToday,
                          classes,
                          props,
                          state.from,
                          state.allContactsLabel
                      )
                    : ''}
            </LineChartPannelContent>
        </Paper>
    );
};

export default compose(withTranslation(), withStyles(styles))(LineChartPannel);
